/** Route slug ↔ `/legal/personal-injury-claims`. */
export const PERSONAL_INJURY_CLAIMS_SLUG = "personal-injury-claims";

export const personalInjuryClaimsDefaultData = {
  heroImage:
    "https://images.unsplash.com/photo-1549924231-f9bfc331c153?auto=format&fit=crop&w=1500&q=80",
  galleryImages: [
    "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=900&q=80",
  ],
  stats: [
    { value: "NIC", label: "Claims & complaints" },
    { value: "MCF", label: "Motor fund route" },
    { value: "Civil", label: "Court tiers" },
  ],
  funnelTagline: "Notify early · document everything · pick the right channel—NIC, fund, or court",
  focusAreas: [
    "Road & motor claims",
    "Life & death benefits",
    "Property, fire & theft",
    "NIC disputes & litigation",
  ],
  primaryCtaHeading: "Get help with your accident or insurance claim",
  introduction: {
    headline: "WHY PERSONAL INJURY & ACCIDENT CLAIMS MATTER IN GHANA",
    lead:
      "Most people only meet the legal side of an accident after the crash, the fire, the robbery, the rejected claim, or the life-policy payout that never arrives. Losses feel sudden; the process is not.",
    problemStarts:
      "Every day, strong claims weaken because of late reporting, missing papers, and not knowing whether to push the insurer, NIC, the Motor Compensation Fund, or the civil courts.",
    frameworkLead: "Ghana’s system expects order: insurers publish procedures; NIC supervises claims handling and complaints; the Motor Compensation Fund backs some road victims when insurance does not pay; and the courts hear serious civil tort and contract disputes.",
    frameworkBullets: [
      "National Insurance Commission (NIC) claims-management guidance for life and non-life insurers—clear procedures, required documents, and complaint handling.",
      "Insurance Act, 2021—including the Motor Compensation Fund (e.g. section 228) for certain motor situations where compensation cannot be obtained from an insurer.",
      "Judicial Service civil routes: District, Circuit, and High Courts, with jurisdiction and value rules that decide where a case belongs.",
    ],
  },
  miniSiteGoals: {
    title: "QUESTIONS THIS HUB SHOULD ANSWER",
    subtitle: "High-intent users are often mid-crisis—injury, death, property loss, or a stalled payout:",
    questions: [
      "What do I need before making an accident claim?",
      "What documents prove injury or property loss?",
      "How do road accident claims work in Ghana?",
      "Can dependants claim when someone dies in an accident?",
      "What if the insurer delays or rejects my claim?",
      "What evidence helps fire, burglary, theft, or property damage claims?",
      "When should I complain to NIC?",
      "When is court the right step instead?",
    ],
  },
  scopeSection: {
    title: "WHAT FALLS UNDER THIS CATEGORY",
    items: [
      "Road traffic accident injury claims",
      "Death claims arising from accidents",
      "Life insurance claims and beneficiary issues",
      "General compensation claims for injury or loss",
      "Property accident and damage claims",
      "Fire, burglary, and theft claims",
      "Insurance dispute resolution (delay, rejection, underpayment)",
      "Help with documentation, demand letters, NIC complaints, and civil action",
    ],
  },
  firstPrinciple: {
    title: "FIRST PRINCIPLE: CLAIM EARLY AND DOCUMENT EVERYTHING",
    lead: "The strength of a claim depends heavily on what you can prove—not on how strongly you feel wronged.",
    nicNote:
      "NIC’s non-life claims guidance expects prompt notification of loss as soon as it occurs or as soon as reasonably practicable, and requires insurers to spell out what documents they need. Waiting lets memories fade, evidence disappear, and positions harden.",
  },
  documentsChecklist: {
    title: "CORE DOCUMENTS TO GATHER",
    items: [
      "Police report (where applicable)",
      "Medical reports and treatment notes",
      "Photos of injuries, vehicles, or damaged property",
      "Hospital bills and receipts",
      "Death certificate where relevant",
      "Letters of administration when claiming for a deceased person’s estate or dependants",
      "Policy schedule and policy wording",
      "Completed claim forms",
      "All correspondence with the insurer (email, letters, SMS logs where useful)",
      "Witness names and contact details",
      "Repair estimates or professional valuation reports",
      "Proof of ownership and value for damaged or stolen property",
    ],
    nicMotorNote:
      "NIC’s motor-complaint materials highlight police and medical reports, two passport photos authenticated by a medical officer, letters of administration for deceased-related claims, and insurer correspondence—treat these as baseline expectations, not optional extras.",
  },
  roadAccidents: {
    title: "ROAD ACCIDENT CLAIMS IN GHANA",
    body:
      "Start by pursuing the insurer or the vehicle owner, as the facts allow. If compensation still cannot be obtained, NIC explains that victims may petition the Motor Compensation Fund— in writing to the Fund’s Secretary or through NIC’s complaints system. The Fund exists under the Insurance Act, 2021 framework to compensate people injured, or dependants of people killed, in specified motor accidents when insurance does not fill the gap.",
  },
  afterAccident: {
    title: "IMMEDIATELY AFTER A ROAD ACCIDENT",
    steps: [
      "Seek medical attention first—health before paperwork.",
      "Report the incident to the police.",
      "Photograph the scene, vehicles, and plates; collect witness contacts.",
      "Notify the insurer as quickly as reasonably practicable.",
      "Retain treatment records, receipts, and every message about the claim.",
      "Do not sign settlement releases you have not read or do not fully understand.",
      "If handling stalls or feels unfair, document it and escalate through NIC where appropriate.",
    ],
  },
  deathClaims: {
    title: "DEATH CLAIMS AFTER ACCIDENTS",
    body:
      "Families are grieving—not drafting—but claims still turn on formal authority and paperwork. NIC’s guidance explicitly raises letters of administration for claims relating to a deceased person. Relationship alone does not replace the lawful right to sue or to receive settlement proceeds; get clarity early on who may act.",
  },
  lifeInsurance: {
    title: "LIFE INSURANCE CLAIMS",
    lead:
      "NIC publishes separate claims-management guidance for life insurers, focused on orderly assessment, settlement discipline, and clear communication. For beneficiaries, this is fundamentally: locate the policy, notify promptly, submit what the tariff requires, and chase written reasons for any delay.",
    checklist: [
      "Who is named beneficiary, or who has lawful standing if intestate?",
      "Was cover in force—premiums paid, no lapse?",
      "What event triggers the benefit (death, disability, etc.)?",
      "What exclusions might apply?",
      "What exhibits does the policy or claim manual demand?",
      "Does someone need letters of administration or probate to collect or sue?",
    ],
  },
  personalInjuryComp: {
    title: "COMPENSATION FOR PERSONAL INJURY",
    body:
      "Beyond the policy route, tort and contract claims may exist depending on fault, employment context, dangerous activity, or faulty services. Ghana’s Judicial Service materials describe ordinary civil filings in District and Circuit Courts for contract and tort within monetary limits; the High Court carries broader jurisdiction and specialist lists. Serious claims need early legal triage so pleadings and evidence bundles match the correct forum.",
  },
  propertyClaims: {
    title: "PROPERTY ACCIDENT CLAIMS",
    lead:
      "From building and stock damage to water ingress and collisions into structures, NIC’s non-life framework expects insurers to display claims steps and document lists publicly.",
    items: [
      "Retain proof of ownership and purchase value",
      "Scene photos plus dated incident reports",
      "Repair quotations and independent valuations",
      "Mitigation measures you took without destroying evidence",
    ],
  },
  fireClaims: {
    title: "FIRE CLAIMS",
    tips: [
      "Notify the insurer immediately; ask for written claim requirements.",
      "Report to the relevant authorities where appropriate.",
      "Photograph damage before major clean-up unless unsafe.",
      "Preserve salvage responsibly and document inspections.",
      "Collect ownership and valuation evidence early—do not destroy proof.",
    ],
  },
  theftClaims: {
    title: "BURGLARY AND THEFT CLAIMS",
    tips: [
      "Police reports detailing date, inventory, and modus operandi.",
      "Receipts, warranties, invoices, inventory lists.",
      "Serial numbers and identifiable marks where possible.",
      "Photos of secured doors/windows if policy conditions require diligence.",
      "Landlord or security correspondence showing prompt reporting.",
    ],
  },
  disputes: {
    title: "WHEN CLAIMS GO WRONG",
    body:
      "Rejections, low offers, unexplained silence, or endless demands for repeats of the same file demoralise people who are already harmed. NIC runs a structured complaint path— submissions in writing to the Commissioner, via email or portal— alongside published contact routes and guidance on motor complaint dossiers.",
  },
  escalateNic: {
    title: "WHEN TO ESCALATE TO NIC",
    items: [
      "The insurer stops responding altogether.",
      "Months pass without a reasoned timeline.",
      "You are asked endlessly for paperwork already delivered (keep proof of submission).",
      "Rejection reasons are vague or contradict the policy wording.",
      "Offers appear plainly disconnected from proven loss.",
      "The process clearly conflicts with the insurer’s stated claims manual.",
    ],
  },
  courtAction: {
    title: "COURT ACTION AND LAWYERS",
    body:
      "Not everything resolves at NIC. High-value bodily injury, dubious liability splits, uninsured tortfeasors, or bad-faith angles may deserve suit. Judicial Service publications explain commencing processes and choosing levels of court—but limitation periods, joinder rules, and expert reports bite quickly; involve counsel early when damages or doctrines are serious.",
  },
  commonProblems: {
    title: "COMMON PROBLEMS GHANAIANS FACE",
    items: [
      "Reporting collisions or fires too late",
      "Driving away without police involvement",
      "Thin medical narration in records",
      "Accepting insurer phone statements without confirming in writing",
      "Assuming cousins can lodge death claims without legal authority",
      "No receipts proving electronics, stock, or machinery",
      "Mixing insurer negotiations with improvised court threats",
      "Half-filled proposal forms resurfacing years later",
      "Never escalating while files rot silently",
    ],
  },
  practicalTips: {
    title: "PRACTICAL TIPS",
    afterAnyIncident: {
      heading: "After any accident",
      items: [
        "Treat first, document second",
        "Trigger official incident reports",
        "Capture photos and credible witnesses",
        "Notify underwriting quickly",
        "Keep every invoice and clinician note",
        "Follow up formally—email beats oral-only hearsay",
      ],
    },
    beforeSettlement: {
      heading: "Before signing settlement releases",
      items: ["Read extinguishment clauses slowly", "Model future physiotherapy costs", "Compare indemnity versus cash offers", "Engage lawyers for catastrophic harm"],
    },
    fireBurglary: {
      heading: "Fire / burglary / theft",
      items: [
        "Inventory expensive goods while you still have receipts",
        "Snapshot damage before clearing debris",
        "Demand insurer checklists by email",
        "Align inventory with forensic schedules",
      ],
    },
    lifeClaim: {
      heading: "Life insurance",
      items: [
        "Find original policy artifacts",
        "Confirm nominated beneficiaries vs intestacy",
        "Obtain authoritative death/medical confirmations",
        "Ask whether ancillary grants are needed early",
      ],
    },
  },
  redFlags: {
    title: "RED FLAGS",
    items: [
      "Adjuster refusing next-step summaries in writing",
      "Rolling delays with no supervisory name",
      "Courier receipts ignored",
      "Pressure to sign before investigations close",
      "Verbal declinatures only",
      "Serious RTA with no police file",
      "Family factions fighting openly over who lodges",
      "Demolishing buildings before insurer survey",
      "No proof pile for supposedly high-value belongings",
    ],
  },
  editorialPlaybook: {
    title: "HIGH-VALUE CONTENT CLUSTER",
    road: ["What to Do After a Road Accident in Ghana", "Motor Accident Claim Checklist", "When to Petition the Motor Compensation Fund"],
    injury: ["How Compensation Works After Injury", "Documents That Strengthen Injury Claims", "When to Settle vs When to Fight"],
    life: ["How to Claim a Life Benefit in Ghana", "What Families Should Do After a Policyholder Dies", "Why Life Claims Stall"],
    property: ["Fire Insurance Claim Checklist", "Burglary & Theft Claim Guide", "Property Damage: Evidence That Wins"],
    disputes: ["How to Complain to NIC", "Rejected Claim: What Next?", "Taking an Insurance Dispute to Court"],
    videos: ["Emergency scene capture", "NIC portal walk-through", "Preparing affidavit bundles"],
    leadCtasHeading: "Suggested CTAs",
    leadCtas: [
      "Start my claim review",
      "Talk to an accident lawyer",
      "Check my claim documents",
      "Help with a rejected claim",
      "Escalate my insurance dispute",
      "Review my settlement offer",
      "Help me claim for a deceased relative",
    ],
  },
  funnelApplication: {
    title: "INFOBOXX FUNNEL APPLICATION",
    hooksHeading: "Hooks",
    hooks: [
      "The insurer has gone quiet.",
      "We lost someone and the payout process is chaotic.",
      "They offered money but something feels off.",
    ],
    education: {
      label: "Education",
      text: "Document stacks, timelines, NIC channels, Motor Fund criteria, beneficiary authority, salvage duty, litigation posture.",
    },
    authority: { label: "Authority", text: "NIC manuals, Insurance Act scaffolding, Judicial Service procedural primers." },
    conversion: {
      label: "Conversion",
      text: "Claim desk reviews, demand letters, complaint drafting, forensic scheduling, courtroom retainers.",
    },
  },
  finalTakeaway: {
    title: "FINAL TAKEAWAY",
    pillars: ["Evidence culture", "Regulator ladders", "No mystery settlements"],
    messageLead: "Claimants lose most often when they:",
    mistakes: ["Wait silently", "Lose artefacts", "Accept oral brush-offs"],
    safeMessage:
      "The safest accident claim is the one started early, documented forensically, and pushed through the correct channel—insurer, Motor Fund, NIC, or court—with advice when stakes rise.",
  },
  bottomLine: {
    title: "BOTTOM LINE",
    lead: "Compensation hinges on paperwork pace and proof—not luck.",
    organised: "Notify · index documents · escalate in writing",
    vs: "vs",
    chaotic: "Ghosting insurers · shredding evidence · oral-only fights",
    closing: "Map your policy route, NIC’s desk, Fund eligibility, then court—with counsel mirroring Judicial Service tiers.",
  },
  faqs: [
    {
      q: "What documents matter for motor claims?",
      a: "Baseline sets include police narratives, clinician reports, visuals of harm, insurer correspondence—and for deceased estates, NIC specifically flags letters of administration plus authenticated claimant photos.",
    },
    {
      q: "What if the insurer is useless?",
      a: "For motor losses NIC describes moving from insurer/vehicle recovery toward written Motor Compensation Fund petitions or formal NIC complaints armed with dossiers proving notification and stalled handling.",
    },
    {
      q: "The victim died—is there still a claim?",
      a: "Often yes, but authority matters: families may need grants of administration or allied court orders before institutions release large sums or before suits continue properly.",
    },
    {
      q: "Can insurers reject claims outright?",
      a: "Yes—policies carry conditions and exclusions. Challenge unclear denials with written reasons, policy exhibits, and NIC escalation when regulatory standards appear breached.",
    },
    {
      q: "Do WhatsApp and email threads count?",
      a: "Digital correspondence frequently proves notice dates, delay, and shifting adjuster positions—export and timestamp them like any other exhibit.",
    },
  ],
  articles: [
    {
      id: "pi-a-1",
      slug: "personal-injury-road-accident-checklist-ghana",
      title: "What to Do After a Road Accident in Ghana",
      excerpt:
        "Scene safety, police reporting, insurer notification windows, medical evidence, and when to shift from insurer recoveries to NIC or the Motor Compensation Fund.",
      type: "Guide",
      readTime: "12 min read",
      author: "InfoBoxx Editorial Desk",
      publishedAt: "Updated recently",
      updatedAt: "Updated recently",
      socialText: "Road accident checklist for Ghana—insurance, NIC, Motor Fund",
      coverImage:
        "https://images.unsplash.com/photo-1549924231-f9bfc331c153?auto=format&fit=crop&w=800&q=80",
      tags: ["Motor", "NIC", "Documents"],
      pullQuote: "Treatment first—then parallel paper trails.",
      takeaways: ["Police + medical file early", "Written insurer notice", "Fund petition when cover fails"],
      video: {
        title: "Emergency scene capture",
        duration: "5:20",
        poster:
          "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        {
          heading: "First 48 hours",
          text: "",
          bullets: ["Hospital priority", "Scene photos", "Insurer intake forms"],
        },
      ],
    },
    {
      id: "pi-a-2",
      slug: "personal-injury-life-insurance-beneficiary-ghana",
      title: "How to Claim a Life Insurance Benefit in Ghana",
      excerpt:
        "Beneficiary proofs, lapse arguments, exclusions, timelines under NIC life-claims supervision, and when estates need probate or letters of administration.",
      type: "Checklist",
      readTime: "10 min read",
      author: "Partner Knowledge Team",
      publishedAt: "This week",
      updatedAt: "This week",
      socialText: "Life claims in Ghana—documents and NIC expectations",
      coverImage:
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
      tags: ["Life", "Beneficiary", "NIC"],
      pullQuote: "Authority to receive funds is never assumed.",
      takeaways: ["Policy archaeology", "Medical cause dossiers", "Written status updates"],
      video: {
        title: "NIC portal cues",
        duration: "6:05",
        poster:
          "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=800&q=80",
      },
      body: [{ heading: "Parallel workflows", text: "Internal insurer dispute file plus regulator-ready exhibit index." }],
    },
    {
      id: "pi-a-3",
      slug: "personal-injury-nic-complaint-fire-theft-dispute",
      title: "Rejected or Delayed Claim? NIC & Court Routes",
      excerpt:
        "Building complaint affidavits, motor-specific NIC annexures, escalation etiquette, salvage disputes, undervaluation, and choosing District vs Circuit vs High Court.",
      type: "Expert Explainer",
      readTime: "11 min read",
      author: "Consumer Guidance Unit",
      publishedAt: "Reviewed this month",
      updatedAt: "Reviewed this month",
      socialText: "Insurance disputes—NIC complaints and Ghana civil courts",
      coverImage:
        "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=800&q=80",
      tags: ["Disputes", "Court", "Property"],
      pullQuote: "Written reasons unlock regulatory leverage.",
      takeaways: ["Chronologies beat anger", "Fund vs tort election", "Limitation audits"],
      video: {
        title: "Affidavit bundles",
        duration: "4:48",
        poster:
          "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80",
      },
      body: [{ heading: "Strategy fork", text: "Regulator escalation may precede or accompany civil suits—sequence with counsel." }],
    },
  ],
  videos: [
    {
      id: "pi-v-1",
      title: "Emergency scene capture",
      duration: "5:20",
      poster:
        "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=900&q=80",
      description: "Metadata hygiene for contemporaneous collision evidence.",
    },
    {
      id: "pi-v-2",
      title: "NIC portal walk-through",
      duration: "6:05",
      poster:
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=900&q=80",
      description: "Attaching annexures NIC expects.",
    },
    {
      id: "pi-v-3",
      title: "Preparing affidavit bundles",
      duration: "4:48",
      poster:
        "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=900&q=80",
      description: "Index filings before civil suits launch.",
    },
  ],
  podcasts: [
    {
      id: "pi-p-1",
      episodeLabel: "Episode 1",
      duration: "32 min",
      title: "Motor Fund vs Own Insurer",
      description: "Sequencing claims when neither party cooperates.",
      host: "Afrimore Legal Desk",
      publishedAt: "Jun 2026",
      coverImage:
        "https://images.unsplash.com/photo-1549924231-f9bfc331c153?auto=format&fit=crop&w=900&q=80",
    },
    {
      id: "pi-p-2",
      episodeLabel: "Episode 2",
      duration: "28 min",
      title: "Letters of Administration in Shock",
      description: "Why grieving families stall without grants.",
      host: "Consumer Guidance Unit · Infoboxx",
      publishedAt: "May 2026",
      coverImage:
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=900&q=80",
    },
    {
      id: "pi-p-3",
      episodeLabel: "Mini",
      duration: "19 min",
      title: "WhatsApp Threads as Evidence",
      description: "Export discipline for disputed timelines.",
      host: "Afrimore Legal Desk",
      publishedAt: "Apr 2026",
      coverImage:
        "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=900&q=80",
    },
  ],
};
